import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getRouteRecommendations } from '../data/trips/routes';
import { places } from '../data/routes.ts';

const icons = {
  camping: '⛺',
};

const findPlaceByName = (name) => places.find(p => p.name === name);

const TripRecommendations = ({ preferences }) => {
  const [showAll, setShowAll] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const matchingRoutes = getRouteRecommendations(preferences);
  const maxToShow = 3;
  const routesToShow = showAll ? matchingRoutes : matchingRoutes.slice(0, maxToShow);

  if (matchingRoutes.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-xl font-semibold text-gray-900 mb-4">
          No encontramos rutas que coincidan exactamente con tus preferencias
        </h3>
        <p className="text-gray-600">
          Pero no te preocupes, estamos trabajando en añadir más rutas. 
          ¿Te gustaría que te avisemos cuando tengamos nuevas rutas disponibles?
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {routesToShow.map(route => {
          // Get first 1-2 overnight spots
          const sleepingSpots = route.itinerary
            .map(day => day.camping)
            .filter(Boolean)
            .slice(0, 2);
          return (
            <div 
              key={route.id}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:border-brand transition-all duration-300 flex flex-col h-full"
            >
              <div className="flex-1 flex flex-col p-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {route.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {route.description}
                  </p>
                  <div className="mb-4">
                    <span className="inline-block text-sm text-gray-500 mr-2">
                      {route.days} días
                    </span>
                    <span className="inline-block text-sm text-gray-500">
                      {route.totalDistance} km
                    </span>
                  </div>
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-gray-500 block mb-1">Destacados:</span>
                    <ul className="flex flex-wrap gap-1">
                      {route.highlights.slice(0, 2).map((highlight, idx) => (
                        <li key={idx} className="inline-block bg-brand/10 text-brand rounded-full px-2 py-1 text-xs">
                          {highlight}
                        </li>
                      ))}
                      {route.highlights.length > 2 && (
                        <li className="inline-block text-xs text-gray-400">+{route.highlights.length - 2} más</li>
                      )}
                    </ul>
                  </div>
                  {/* Sleeping spots preview */}
                  {sleepingSpots.length > 0 && (
                    <div className="mb-4">
                      <span className="text-xs font-semibold text-gray-500 block mb-1">Pernoctas:</span>
                      <ul className="space-y-1">
                        {sleepingSpots.map((spot, idx) => (
                          <li key={idx} className="text-xs text-gray-700 flex items-center gap-1">
                            <span>{icons.camping}</span>
                            <span className="font-semibold">{spot.name}</span> ({spot.type})
                            {spot.facilities && (
                              <span className="text-gray-500 ml-1">[{spot.facilities.join(', ')}]</span>
                            )}
                            {findPlaceByName(spot.name)?.googleMapsUrl && (
                              <a
                                href={findPlaceByName(spot.name).googleMapsUrl.replace(/^@/, '')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-brand underline ml-2"
                              >
                                Mapa
                              </a>
                            )}
                          </li>
                        ))}
                        {route.itinerary.filter(day => day.camping).length > 2 && (
                          <li className="text-xs text-gray-400">…</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="mt-auto">
                  <button
                    onClick={() => navigate(`/resources/trip-planner/route/${route.id}`, { state: { from: location.pathname + location.search } })}
                    className="w-full bg-brand text-white font-medium py-2 px-4 rounded-xl hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 transition-all duration-300"
                  >
                    Ver detalles
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {matchingRoutes.length > maxToShow && !showAll && (
        <div className="text-center mt-8">
          <button
            onClick={() => setShowAll(true)}
            className="inline-block px-6 py-3 bg-gray-100 text-brand font-semibold rounded-xl hover:bg-brand/10 transition-all duration-300"
          >
            Ver todas las rutas recomendadas
          </button>
        </div>
      )}
    </>
  );
};

export default TripRecommendations; 